import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { TRANSIENT_SURFACE_RADIUS } from '~/theme';

import {
  isUnitBeyondMaxSpan,
  isUnitDisabled,
  MAX_RANGE_MONTHS,
  MONTH_COUNT,
  type PeriodUnit,
  QUARTER_COUNT,
  resolveUnitRange,
} from './periodRange';
import type { Granularity, SelectedDateRange } from './types';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const MONTH_FULL_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const QUARTER_LABELS = ['Q1', 'Q2', 'Q3', 'Q4'];

type PeriodGridProps = {
  granularity: Granularity;
  year: number;
  onYearChange: (year: number) => void;
  range: SelectedDateRange;
  maxDate: Date;
  onSelectUnit: (unit: PeriodUnit) => void;
};

function unitLabel(granularity: Granularity, index: number): string {
  return granularity === 'month' ? MONTH_LABELS[index] : QUARTER_LABELS[index];
}

function unitAriaLabel(granularity: Granularity, index: number, year: number): string {
  return granularity === 'month'
    ? `${MONTH_FULL_LABELS[index]} ${year}`
    : `${QUARTER_LABELS[index]} ${year}`;
}

function isUnitWithinRange(unitRange: { start: Date; end: Date }, range: SelectedDateRange) {
  if (!range.start || !range.end) {
    return false;
  }
  return unitRange.start >= range.start && unitRange.end <= range.end;
}

function PeriodGrid({
  granularity,
  year,
  onYearChange,
  range,
  maxDate,
  onSelectUnit,
}: PeriodGridProps) {
  const theme = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoveredGranularity, setHoveredGranularity] = useState<Granularity>(granularity);
  const unitCount = granularity === 'month' ? MONTH_COUNT : QUARTER_COUNT;
  const columns = granularity === 'month' ? 3 : 4;

  // Year navigation stops where every unit beyond it is out of span of a one-unit draft,
  // so the grid can't be paged into years that can no longer be chosen.
  const lastUnit = unitCount - 1;
  const previousYearOutOfSpan = isUnitBeyondMaxSpan(
    { granularity, year: year - 1, index: lastUnit },
    range,
  );
  const nextYearOutOfSpan = isUnitBeyondMaxSpan({ granularity, year: year + 1, index: 0 }, range);

  // A stale index from the other tab's grid would highlight the wrong cell
  // after a tab switch, since index 3 means a different unit in each grid.
  if (hoveredGranularity !== granularity) {
    setHoveredGranularity(granularity);
    setHoveredIndex(null);
  }

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <IconButton
          aria-label="Previous year"
          size="small"
          disabled={previousYearOutOfSpan}
          onClick={() => onYearChange(year - 1)}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" component="span" fontWeight={600}>
          {year}
        </Typography>
        <IconButton
          aria-label="Next year"
          size="small"
          disabled={nextYearOutOfSpan}
          onClick={() => onYearChange(year + 1)}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        role="group"
        aria-label={`${granularity === 'month' ? 'Months' : 'Quarters'} in ${year}`}
        display="grid"
        gridTemplateColumns={`repeat(${columns}, 1fr)`}
        gap={1}
      >
        {Array.from({ length: unitCount }, (_, index) => {
          const unit: PeriodUnit = { granularity, year, index };
          const unitRange = resolveUnitRange(unit);
          const disabled = isUnitDisabled(unit, maxDate, range);
          const selected = isUnitWithinRange(unitRange, range);
          const isRangeStart = Boolean(
            range.start && unitRange.start.getTime() === range.start.getTime(),
          );
          const isRangeEnd = Boolean(range.end && unitRange.end.getTime() === range.end.getTime());
          const isEndpoint = selected && (isRangeStart || isRangeEnd);
          const hovered = hoveredIndex === index && !disabled;

          return (
            <ButtonBase
              key={index}
              disabled={disabled}
              onClick={() => onSelectUnit(unit)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              aria-label={unitAriaLabel(granularity, index, year)}
              aria-pressed={selected}
              sx={{
                minHeight: 44,
                // Not a pill — that would break the band a selected range draws across cells.
                borderRadius: `${TRANSIENT_SURFACE_RADIUS}px`,
                typography: 'body2',
                fontWeight: selected ? 600 : 400,
                color: disabled
                  ? theme.palette.text.disabled
                  : isEndpoint
                    ? theme.palette.primary.contrastText
                    : selected
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                // Endpoints get a solid fill so they read as distinct from the
                // light band between them: 5.66:1 against the middle fill
                // (rgb(27,86,187) vs rgb(228,235,247)), well past the 3:1
                // floor for a meaningful UI state. The old two-alpha design
                // (0.22 vs 0.12) measured 1.17:1.
                backgroundColor: isEndpoint
                  ? theme.palette.primary.main
                  : selected
                    ? alpha(theme.palette.primary.main, 0.12)
                    : hovered
                      ? theme.palette.action.hover
                      : 'transparent',
                border: '1px solid',
                borderColor: selected ? alpha(theme.palette.primary.main, 0.4) : 'transparent',
                transition: 'background-color 120ms ease, color 120ms ease',
                '&:focus-visible': {
                  // A neutral ring, not primary.main, so focus never reads as
                  // an endpoint marker (or disappears against one).
                  outline: `2px solid ${theme.palette.text.primary}`,
                  outlineOffset: 2,
                },
              }}
            >
              {unitLabel(granularity, index)}
            </ButtonBase>
          );
        })}
      </Box>

      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5, mb: 0 }}>
        Periods can span up to {MAX_RANGE_MONTHS / 12} years.
      </Typography>
    </Box>
  );
}

export default PeriodGrid;
